import React, { FC, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import Label from '../../../../library/inputs/label/Label';
import { useIntl } from 'react-intl';
import TextInput from '../../../../library/inputs/text/TextInput';
import { Coupon, validateDiscountCode } from '../../../../../services/form/FormService';
import { Discount } from '../../../../../models/form/form';
import { AxiosResponse } from 'axios';

interface Props {
  discount: Discount;
  organizationId: string;
  formId: string;
  onApplyDiscount: (discount: Discount | null) => void;
  className: string;
}

const DiscountCode: FC<Props> = ({ discount, onApplyDiscount, organizationId, formId, className }) => {
  const intl = useIntl();
  const [code, setCode] = useState<string>('');
  const [valid, setValid] = useState<boolean>(false);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);

  useEffect(() => {
    if (!discount) {
      setCode('');
      setValid(false);
      setShowFeedback(false);
    }
  }, [discount]);

  const applyDiscountCode = async (discountCode) => {
    if (validating) return;

    setValidating(true);
    const discount: AxiosResponse<Coupon | null> = await validateDiscountCode(organizationId, formId, discountCode);

    if (discount.data) {
      const { discountType: type, discountValue: value } = discount.data;
      onApplyDiscount({ code: discountCode, type, value });
      setValid(true);
    } else {
      setValid(false);
    }

    setShowFeedback(true);
    setValidating(false);
  };

  const removeDiscountCode = () => {
    setValid(false);
    setShowFeedback(false);
    setCode('');
    onApplyDiscount(null);
  };

  return (
    <div className={`discount-code ${className}`}>
      <div className="form-group pt-3 mb-2">
        <Label label={intl.formatMessage({ id: 'formPublic.discountCode' })}></Label>

        {valid && code && (
          <div className="d-flex align-items-center">
            <span className="field-value">{code}</span>
            <Button className="ms-4" onClick={() => removeDiscountCode()} variant="danger">
              {intl.formatMessage({ id: 'formPublic.remove' })}
            </Button>
          </div>
        )}
        {!valid && (
          <div className="d-flex">
            <TextInput onChange={(event) => setCode(event.target.value.toUpperCase())} value={code}></TextInput>
            <Button className="action-button ms-2" onClick={() => applyDiscountCode(code)} variant="primary">
              <span>
                {validating ? <Spinner animation="border" size="sm" /> : intl.formatMessage({ id: 'formPublic.apply' })}
              </span>
            </Button>
          </div>
        )}
        {showFeedback && !valid && (
          <div className="mt-1">
            <div className="feedback-invalid" data-testid="feedback-invalid">
              {intl.formatMessage({ id: 'formPublic.discountCodeError' })}
            </div>
          </div>
        )}
        {discount && showFeedback && valid && (
          <div className="mt-1">
            <div className="feedback-valid" data-testid="feedback-invalid">
              {intl.formatMessage(
                { id: 'formPublic.discountCodeApplied' },
                { discount: discount.value, type: discount.type },
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountCode;
